import 'cross-fetch/polyfill';
import { dictionaryServiceFactory } from './lib/dictionary-service-factory';
import { webStorage } from '@/utils/web-storage';
import { S_I18N } from '@/utils/web-storage-keys';

function getCreateI18nFactory() {
  const vueI18nModule = require('vue-i18n');

  return (
    vueI18nModule?.createI18n ||
    vueI18nModule?.default?.createI18n ||
    vueI18nModule?.default ||
    null
  );
}

/**
 * Initializes the vue-i18n library to provide a translation dictionary to the app.
 * If your app is not multilingual, this file and references to it can be removed.
 * Elsewhere in the app to use the dictionary `{{ $t('styleguide-sample') }}`
 * If you want to use translation inside the function use:
 * ```
 * import { useI18n } from 'vue-i18n';
 * const { t } = useI18n();
 * const text = t('page-not-found');
 * ```
 * @param {string} language Optional, the initial language. Only used for SSR; otherwise language set in RouteHandler.
 * @param {*} dictionary Optional, the dictionary to load. Only used for SSR; otherwise, the dictionary is loaded via JSS dictionary service.
 */
export default function i18nInit(language, dictionary) {
  return new Promise((resolve) => {
    const createI18n = getCreateI18nFactory();
    if (!createI18n) {
      resolve(null);
      return;
    }
    // We are in SSR, dictionary is preloaded. Iniitialize it
    if (dictionary) {
      const i18n = createI18n({
        fallbackLocale: false,
        legacy: false,
        messages: {
          [language]: dictionary,
        },
        locale: language,
      });

      resolve(i18n);
    } else {
      const cache = webStorage.get(S_I18N) || {};
      if (cache[language] && cache[language].length > 0) {
        resolve(
          createI18n({
            fallbackLocale: false,
            legacy: false,
            messages: {
              [language]: cache[language],
            },
            locale: language,
          })
        );
      } else {
        const dictionaryServiceInstance = dictionaryServiceFactory.create();
        dictionaryServiceInstance.fetchDictionaryData(language).then((phrases) => {
          cache[language] = phrases;
          webStorage.set(S_I18N, cache);
          resolve(
            createI18n({
              fallbackLocale: false,
              legacy: false,
              messages: {
                [language]: phrases,
              },
              locale: language,
            })
          );
        });
      }
    }
  });
}
